'use client'

import { IconPhotoFill } from '@macarongfactory/web-icons'
import { Image, Text } from '@macarongfactory/web-ui'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useRef, useState } from 'react'

import { getCarHubImageUrl } from '@/entities/car'
import { MIN_TOP_VEHICLES_TO_SHOW } from '@/features/seoPlaceDetail/lib'
import { useImpressionLog } from '@/features/seoPlaceDetail/model'
import { type IPlaceTopVehicle } from '@/features/seoPlaceDetail/model'
import { logEvent } from '@/shared/external/firebase/analytics'
import { CAR_HUB_ONELINK_CONFIG, useOneLink } from '@/shared/external/onelink'
import { CODE, EVENT_NAMES, LABELS, TYPE } from '@/shared/lib'

import PlaceSectionDivider from '../PlaceSectionDivider/PlaceSectionDivider'

const SECTION_IMPRESSION_PARAMS = {
  code: CODE.COMMON_WEB_PLACE.TOP_CAR,
  type: TYPE.SECTION,
  label: LABELS.COMMON_WEB_PLACE.TOP_CAR,
}

interface IPlaceTopVehiclesProps {
  cars: IPlaceTopVehicle[]
}

const PlaceTopVehicles = ({ cars }: IPlaceTopVehiclesProps) => {
  const sectionRef = useRef<HTMLElement | null>(null)
  const isVisible = cars.length >= MIN_TOP_VEHICLES_TO_SHOW

  useImpressionLog({
    ref: sectionRef,
    eventName: EVENT_NAMES.COMMON_WEB_PLACE.SECTION.SHOW,
    params: SECTION_IMPRESSION_PARAMS,
    enabled: isVisible,
  })

  if (!isVisible) return null

  return (
    <>
      <PlaceSectionDivider />
      <section
        ref={sectionRef}
        aria-label='이 정비소 TOP 차종'
        className='flex flex-col gap-dimension-x4 bg-background-surface-default px-dimension-x5 py-dimension-x6'
      >
        <Text
          as='h2'
          variant='title-1-semibold'
          color='text-foreground-neutral-strong'
        >
          이 정비소 TOP 차종
        </Text>

        <div className='flex flex-col gap-dimension-x4'>
          {cars.map((car, index) => (
            <TopVehicleItem
              key={`${car.companyName}-${car.modelName ?? car.carName}-${index}`}
              rank={index + 1}
              car={car}
            />
          ))}
        </div>
      </section>
    </>
  )
}

interface ITopVehicleItemProps {
  rank: number
  car: IPlaceTopVehicle
}

const TopVehicleItem = ({ rank, car }: ITopVehicleItemProps) => {
  const router = useRouter()
  const { generateLink } = useOneLink(CAR_HUB_ONELINK_CONFIG)
  const [hasImageError, setHasImageError] = useState(false)

  const fullName = [car.companyName, car.modelName ?? car.carName]
    .filter(Boolean)
    .join(' ')
  // 이미지는 세부 모델명(modelName) 우선 사용. modelName이 null인 구버전
  // 데이터는 부모 차종명(carName)으로 fallback.
  const imageNameKey = car.modelName ?? car.carName
  const imageUrl =
    car.companyName && imageNameKey
      ? getCarHubImageUrl(car.companyName, imageNameKey)
      : null
  const showFallback = !imageUrl || hasImageError

  // 차종 허브 경로는 서버에서 차량별 매핑 유종으로 미리 계산해 전달받는다.
  const { carHubPath } = car

  /** SEO 위해 Link href는 정적 경로 유지(크롤러가 인지). 클릭 시점에 SmartScript로
   * 동적 OneLink 생성해 ?seoOnelink= 로 propagate — 차종 허브 CTA가 검색 origin
   * 추적을 이어가도록. */
  const handleClick = async (event: React.MouseEvent<HTMLAnchorElement>) => {
    logEvent(EVENT_NAMES.COMMON_WEB_PLACE.ITEM.CLICK, {
      code: CODE.COMMON_WEB_PLACE.TOP_CAR,
      type: TYPE.ITEM,
      label: fullName,
    })
    if (!carHubPath) return
    event.preventDefault()
    // af_sub3에 차종 ID를 주입해 차종 검색→상세와 동일하게 차종 단위로 집계 (WEB-4236)
    const onelinkUrl = await generateLink({ afSub3: car.carHubId ?? undefined })
    const finalPath = onelinkUrl
      ? `${carHubPath}?seoOnelink=${encodeURIComponent(onelinkUrl)}`
      : carHubPath
    router.push(finalPath)
  }

  const content = (
    <>
      <div className='flex items-center gap-dimension-x4'>
        <div className='flex size-[32px] shrink-0 items-center justify-center overflow-clip rounded-dimension-x2-5 bg-background-surface-basement'>
          {/* TODO: 피그마 Title/3_Medium 14/21. DS title-3 토큰 없어 body-1-medium(14/21.5)로 근사 */}
          <Text
            variant='body-1-medium'
            color='text-foreground-neutral-alternative'
          >
            {rank}
          </Text>
        </div>
        <div className='relative h-[41px] w-[72px] shrink-0 overflow-hidden'>
          {showFallback ? (
            <div className='flex size-full items-center justify-center rounded-dimension-x1-5 bg-background-neutral-weak text-foreground-neutral-muted'>
              <IconPhotoFill width={20} height={20} />
            </div>
          ) : (
            <Image
              src={imageUrl}
              alt={fullName}
              width='100%'
              height='100%'
              radius={0}
              scaletype='fit'
              className='absolute inset-0'
              onError={() => setHasImageError(true)}
            />
          )}
        </div>
      </div>
      <div className='flex min-w-0 flex-1 items-center justify-end gap-dimension-x1 text-right'>
        <Text
          variant='title-2-semibold'
          color='text-foreground-neutral-strong'
          className='truncate'
        >
          {fullName}
        </Text>
      </div>
    </>
  )

  // modelName 없는 구버전 데이터는 라우팅 대상이 없어 div로 렌더 (시각만 동일).
  if (!carHubPath) {
    return <div className='flex items-center gap-dimension-x2'>{content}</div>
  }

  return (
    <Link
      href={carHubPath}
      onClick={handleClick}
      className='flex items-center gap-dimension-x2'
    >
      {content}
    </Link>
  )
}

export default PlaceTopVehicles
